import { useId } from "react";
import { Textarea } from "@/components/ui/textarea";
import type { QuoteDrawerDraft } from "./useQuoteDrawerDraft";

interface NotesSectionProps {
  draft: Pick<QuoteDrawerDraft, "editing" | "fields" | "set">;
  /** The client's first name, for "For {first}". */
  first: string;
}

function Note({ id, title, hint, value, editing, onChange }: {
  id: string; title: string; hint: string; value: string; editing: boolean; onChange: (v: string) => void;
}) {
  return (
    <div className="grid gap-1 text-[12.5px] font-semibold">
      <label htmlFor={editing ? id : undefined}>
        {title} <span className="font-normal text-muted-foreground">{hint}</span>
      </label>
      {editing ? (
        <Textarea id={id} rows={3} value={value} onChange={(e) => onChange(e.target.value)} className="text-[13.5px] font-normal" />
      ) : (
        <div className="min-h-10 whitespace-pre-line rounded-lg bg-background px-2.5 py-2 text-[13.5px] font-normal">
          {value.trim() ? value : <span className="text-muted-foreground">None</span>}
        </div>
      )}
    </div>
  );
}

/** "For {first}" (shown on the quote) and "Staff only" (never shown), #1559. */
export function NotesSection({ draft, first }: NotesSectionProps) {
  const id = useId();
  return (
    <section data-section="notes" className="rounded-[10px] border bg-card">
      <div className="border-b px-3.5 py-2.5">
        <h3 className="font-display text-[15px] font-bold">Notes</h3>
      </div>
      <div className="grid grid-cols-2 gap-2.5 px-3.5 py-3 max-[760px]:grid-cols-1">
        <Note id={`${id}-notes`} title={`For ${first}`} hint="shown on the quote" value={draft.fields.notes}
          editing={draft.editing} onChange={(v) => draft.set("notes", v)} />
        <Note id={`${id}-staff`} title="Staff only" hint="never shown" value={draft.fields.internalNotes}
          editing={draft.editing} onChange={(v) => draft.set("internalNotes", v)} />
      </div>
    </section>
  );
}
